import { currentThemeSchemaVersion, } from "./contracts";
const versionParts = (version) => version.split(".").map((part) => Number.parseInt(part, 10) || 0);
export const compareThemeVersions = (first, second) => {
    const firstParts = versionParts(first);
    const secondParts = versionParts(second);
    const length = Math.max(firstParts.length, secondParts.length);
    for (let index = 0; index < length; index += 1) {
        const difference = (firstParts[index] ?? 0) - (secondParts[index] ?? 0);
        if (difference !== 0)
            return Math.sign(difference);
    }
    return 0;
};
const migrateV1ToV2 = {
    migrationId: "theme-preference-1.0-to-2.0",
    fromVersion: "1.0",
    toVersion: "2.0",
    migrate: (preference, timestamp) => {
        const unknown = [...(preference.unknownProperties ?? [])];
        const diagnostics = [];
        const values = { ...preference.values };
        const fontScaleIndex = unknown.findIndex((property) => property.key === "typography.fontScale");
        const fontScale = unknown[fontScaleIndex];
        if (fontScale &&
            typeof fontScale.value === "number" &&
            values["typography.scale"] === undefined) {
            values["typography.scale"] = fontScale.value;
            unknown.splice(fontScaleIndex, 1);
            diagnostics.push("Migrated typography.fontScale to typography.scale.");
        }
        const cornerIndex = unknown.findIndex((property) => property.key === "shape.corners");
        const corners = unknown[cornerIndex];
        if (corners &&
            (corners.value === "square" ||
                corners.value === "soft" ||
                corners.value === "rounded")) {
            const radii = corners.value === "square"
                ? [0, 0, 0]
                : corners.value === "rounded"
                    ? [12, 16, 24]
                    : [6, 10, 16];
            const [small = 6, medium = 10, large = 16] = radii;
            values["shape.radius.small"] ??= small;
            values["shape.radius.medium"] ??= medium;
            values["shape.radius.large"] ??= large;
            unknown.splice(cornerIndex, 1);
            diagnostics.push("Migrated shape.corners to semantic radius tokens.");
        }
        const migrated = {
            ...preference,
            schemaVersion: "2.0",
            values,
            unknownProperties: unknown,
            compatibility: {
                ...preference.compatibility,
                schemaVersion: "2.0",
                migratedFromVersion: preference.schemaVersion,
                migrationStatus: "migrated",
                migrationTimestamp: timestamp,
            },
        };
        return {
            status: "migrated",
            preference: migrated,
            original: preference,
            diagnostics,
        };
    },
};
const noMigration = (preference, status, diagnostics) => ({
    status,
    preference,
    original: preference,
    diagnostics,
});
export const themeMigrationRegistry = {
    currentVersion: currentThemeSchemaVersion,
    migrations: [migrateV1ToV2],
    migrate: (preference, timestamp) => {
        const comparison = compareThemeVersions(preference.schemaVersion, currentThemeSchemaVersion);
        if (comparison === 0) {
            return noMigration(preference, "not-required", []);
        }
        if (comparison > 0) {
            return noMigration(preference, "unsupported-future", [
                `Theme schema ${preference.schemaVersion} is newer than supported ${currentThemeSchemaVersion}.`,
            ]);
        }
        let current = preference;
        const original = preference;
        const diagnostics = [];
        const visited = new Set();
        while (current.schemaVersion !== currentThemeSchemaVersion) {
            if (visited.has(current.schemaVersion)) {
                return {
                    status: "failed",
                    preference: original,
                    original,
                    diagnostics: [...diagnostics, "Theme migration cycle detected."],
                };
            }
            visited.add(current.schemaVersion);
            const migration = themeMigrationRegistry.migrations.find((candidate) => candidate.fromVersion === current.schemaVersion);
            if (!migration) {
                return {
                    status: "failed",
                    preference: original,
                    original,
                    diagnostics: [
                        ...diagnostics,
                        `No migration exists from ${current.schemaVersion}.`,
                    ],
                };
            }
            const result = migration.migrate(current, timestamp);
            if (result.status === "failed") {
                return {
                    status: "failed",
                    preference: original,
                    original,
                    diagnostics: [...diagnostics, ...result.diagnostics],
                };
            }
            current = result.preference;
            diagnostics.push(...result.diagnostics);
        }
        return {
            status: "migrated",
            preference: current,
            original,
            diagnostics,
        };
    },
};
export const preserveUnknownThemeProperties = (preference, additions) => ({
    ...preference,
    unknownProperties: [
        ...(preference.unknownProperties ?? []),
        ...additions,
    ],
});
//# sourceMappingURL=migration.js.map